import React, { useEffect } from "react";
import styled from "styled-components";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { UIState } from "../types/ui-types";
import { Checkbox } from "primereact/checkbox";

const FooterContainer = styled.div`
  width: 100%;
  height: 100px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  background-color: #f8f9fa;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  min-height: 100px;
  position: sticky;
  bottom: 0;
  z-index: 1000;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

interface ReportFooterProps {
  handleNextStep: () => void;
  nextStepLabel?: string;
  showConfirmationCheckbox?: boolean;
  proceedDisabled?: boolean;
  displayEcomodError?: boolean;
  isLoading?: boolean;
  disableNextButton?: boolean;
  secondaryAction?: React.ReactNode;
  isLightReport?: boolean;
  saveDraft?: () => void;
}

const ReportFooter: React.FC<ReportFooterProps> = ({
  handleNextStep,
  nextStepLabel,
  showConfirmationCheckbox = false,
  proceedDisabled = false,
  displayEcomodError = false,
  isLoading = false,
  disableNextButton = false,
  secondaryAction,
  isLightReport = false,
  saveDraft,
}: ReportFooterProps) => {
  const { t } = useTranslation();
  const [toast] = useUIStore(useShallow((state: UIState) => [state.toastRef]));
  const [confirmed, setConfirmed] = React.useState(true);

  const handleSaveDraft = () => {
    // Save draft here

    saveDraft
      ? saveDraft()
      : toast?.current?.show({
          severity: "success",
          summary: t("message-draft-saved"),
          life: 3000,
          closable: false,
        });
  };

  useEffect(() => {
    if (showConfirmationCheckbox) {
      setConfirmed(false);
    } else {
      setConfirmed(true);
    }
  }, [showConfirmationCheckbox]);

  return (
    <FooterContainer>
      <ActionButtons>
        <Button
          role="button"
          type="button"
          outlined
          label={t("cmd-save-draft")}
          onClick={(e) => {
            e.stopPropagation();
            handleSaveDraft();
          }}
        />
      </ActionButtons>
      {showConfirmationCheckbox && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginLeft: "auto",
            marginRight: "20px",
          }}
        >
          <Checkbox
            inputId="confirmation"
            onChange={(e) => setConfirmed(!!e.checked)}
            checked={confirmed}
          />
          <label htmlFor="confirmation">
            {t("label-i-confirm-this-information")}
          </label>
        </div>
      )}
      {displayEcomodError && (
        <div
          style={{
            color: "red",
            fontSize: "0.8rem",
            marginLeft: "auto",
            marginRight: "20px",
            maxWidth: "500px",
          }}
        >
          {isLightReport
            ? t("text-ecomod-error-light")
            : t("text-ecomod-error")}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {secondaryAction}
        <Button
          id="next-step-button"
          style={{ marginRight: "80px" }}
          label={nextStepLabel}
          onClick={handleNextStep}
          disabled={
            !confirmed || proceedDisabled || isLoading || disableNextButton
          }
          loading={isLoading}
        />
      </div>
    </FooterContainer>
  );
};

export default ReportFooter;
